// src/AdminDashboard.js
//
// Pannello MIA — Dashboard Super Admin
// Visibile solo al Super Admin nel Master HR.
// Mostra tutti i centri con stato abbonamento, SMS, features.
// Azioni: impersona, aggiungi SMS, toggle DICOM, sospendi/riattiva.

import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useUser } from './UserContext';

// Inizializzazione Functions
const functions = getFunctions(undefined, 'europe-west1');

// Colori stato
const statusColors = {
  active:    { bg: '#dcfce7', text: '#166534', label: 'Attivo' },
  trialing:  { bg: '#dbeafe', text: '#1e40af', label: 'Trial' },
  past_due:  { bg: '#fef3c7', text: '#92400e', label: 'Past Due' },
  suspended: { bg: '#fee2e2', text: '#991b1b', label: 'Sospeso' },
  cancelled: { bg: '#f3f4f6', text: '#6b7280', label: 'Cancellato' },
  no_subscription: { bg: '#f3f4f6', text: '#9ca3af', label: 'No Abb.' },
};

export default function AdminDashboard() {
  const { userProfile } = useUser();
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(null); // "centerId:action"

  // 🆕 Creazione nuovo centro
  const [newCenterModal, setNewCenterModal] = useState(false);
  const [newCenterName, setNewCenterName] = useState('');
  const [newOwnerName, setNewOwnerName] = useState('');
  const [newOwnerEmail, setNewOwnerEmail] = useState('');
  const [newCenterLoading, setNewCenterLoading] = useState(false);
  const [newCenterResult, setNewCenterResult] = useState(null); // { centerId } dopo la creazione

  // Carica i dati di tutti i centri
  const loadDashboard = async () => {
    setLoading(true);
    setError('');
    try {
      const fn = httpsCallable(functions, 'getAdminDashboardData');
      const result = await fn();
      setCenters(result.data.centers || []);
    } catch (err) {
      console.error('Errore caricamento dashboard:', err);
      setError(err.message || 'Errore nel caricamento dei dati.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  // ── AZIONE: Impersona ─────────────────────────────────────────────────
  // Apre il gestionale scelto in una nuova tab, passando ?impersonate={centerId}.
  // Vale SOLO per quella tab/navigazione: se ricarichi senza il parametro
  // (o navighi altrove), torni al tuo centro normale — non viene salvato
  // da nessuna parte.
  const handleImpersonate = (centerId, app) => {
    const domain = app === 'hr' ? 'https://auramedical.club' : 'https://clinico.auramedical.club';
    window.open(`${domain}/?impersonate=${centerId}`, '_blank');
  };

  // ── AZIONE: Toggle DICOM ──────────────────────────────────────────────
  const handleToggleDicom = async (centerId, currentValue) => {
    const key = `${centerId}:dicom`;
    setActionLoading(key);
    try {
      const fn = httpsCallable(functions, 'toggleFeatureOverride');
      await fn({ centerId, featureName: 'dicomEnabled', value: !currentValue });
      await loadDashboard(); // Ricarica
    } catch (err) {
      alert('Errore: ' + (err.message || 'Riprova'));
    } finally {
      setActionLoading(null);
    }
  };

  // ── AZIONE: Sospendi / Riattiva ───────────────────────────────────────
  const handleSuspendToggle = async (centerId, currentStatus) => {
    const action = currentStatus === 'suspended' ? 'reactivate' : 'suspend';
    const confirmMsg = action === 'suspend'
      ? 'Sei sicuro di voler SOSPENDERE questo centro? Lo staff non potrà più accedere.'
      : 'Vuoi RIATTIVARE questo centro?';

    if (!window.confirm(confirmMsg)) return;

    const key = `${centerId}:suspend`;
    setActionLoading(key);
    try {
      const fn = httpsCallable(functions, 'suspendCenter');
      await fn({ centerId, action });
      await loadDashboard();
    } catch (err) {
      alert('Errore: ' + (err.message || 'Riprova'));
    } finally {
      setActionLoading(null);
    }
  };

  // ── AZIONE: Crea nuovo centro ─────────────────────────────────────────
  const handleCreateCenter = async () => {
    if (!newCenterName.trim() || !newOwnerName.trim() || !newOwnerEmail.trim()) return;

    setNewCenterLoading(true);
    try {
      const fn = httpsCallable(functions, 'registerNewCenter');
      const result = await fn({
        centerName: newCenterName.trim(),
        ownerName: newOwnerName.trim(),
        ownerEmail: newOwnerEmail.trim(),
      });
      setNewCenterResult({ centerId: result.data.centerId });
      await loadDashboard();
    } catch (err) {
      alert('Errore: ' + (err.message || 'Riprova'));
    } finally {
      setNewCenterLoading(false);
    }
  };

  const closeNewCenterModal = () => {
    setNewCenterModal(false);
    setNewCenterName('');
    setNewOwnerName('');
    setNewOwnerEmail('');
    setNewCenterResult(null);
  };

  // ── STATS AGGREGATE ───────────────────────────────────────────────────
  const totalCenters = centers.length;
  const activeCenters = centers.filter(c => c.status === 'active' || c.status === 'trialing').length;
  const pastDueCenters = centers.filter(c => c.status === 'past_due').length;
  const suspendedCenters = centers.filter(c => c.status === 'suspended').length;
  const totalSmsSent = centers.reduce((sum, c) => sum + (c.smsSentThisMonth || 0), 0);

  // ── RENDER ────────────────────────────────────────────────────────────
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [saChecked, setSaChecked] = useState(false);

  useEffect(() => {
    if (!userProfile?.uid) return;
    const checkSA = async () => {
      const { doc: fbDoc, getDoc } = await import('firebase/firestore');
      const { db: fbDb } = await import('./firebase');
      const snap = await getDoc(fbDoc(fbDb, 'superAdmins', userProfile.uid));
      setIsSuperAdmin(snap.exists());
      setSaChecked(true);
    };
    checkSA();
  }, [userProfile?.uid]);

  if (!saChecked) {
    return (
      <div style={{ padding: '60px', textAlign: 'center', color: '#6b7280' }}>
        Verifica permessi...
      </div>
    );
  }

  if (!isSuperAdmin) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#991b1b' }}>
        <h2>Accesso Negato</h2>
        <p>Questa sezione è riservata al Super Admin.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: '60px', textAlign: 'center', color: '#6b7280' }}>
        <div style={{ fontSize: '1.5rem', marginBottom: '10px' }}>⏳</div>
        Caricamento dashboard...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ color: '#991b1b', marginBottom: '16px' }}>❌ {error}</div>
        <button onClick={loadDashboard} style={btnStyle('#2563eb')}>Riprova</button>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '1.6rem', fontWeight: '700', color: '#0f172a', margin: 0 }}>
            👑 Pannello MIA
          </h1>
          <p style={{ color: '#64748b', fontSize: '0.9rem', marginTop: '4px' }}>
            Gestione centralizzata di tutti i centri Aura
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => setNewCenterModal(true)} style={btnStyle('#16a34a')}>
            + Nuovo Centro
          </button>
          <button onClick={loadDashboard} style={btnStyle('#6366f1')}>
            🔄 Aggiorna
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '24px' }}>
        <StatCard label="Centri Totali" value={totalCenters} color="#3b82f6" />
        <StatCard label="Attivi" value={activeCenters} color="#10b981" />
        <StatCard label="Past Due" value={pastDueCenters} color="#f59e0b" />
        <StatCard label="Sospesi" value={suspendedCenters} color="#ef4444" />
        <StatCard label="SMS Inviati (mese)" value={totalSmsSent} color="#8b5cf6" />
      </div>

      {/* Tabella Centri */}
      <div style={{
        backgroundColor: 'white', borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden',
        border: '1px solid #e5e7eb',
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e5e7eb' }}>
                <th style={thStyle}>Centro</th>
                <th style={thStyle}>Stato</th>
                <th style={thStyle}>Piano</th>
                <th style={thStyle}>SMS (mese)</th>
                <th style={thStyle}>DICOM</th>
                <th style={thStyle}>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {centers.map((center) => {
                const st = statusColors[center.status] || statusColors.no_subscription;

                return (
                  <tr key={center.centerId} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    {/* Nome */}
                    <td style={tdStyle}>
                      <div style={{ fontWeight: '600', color: '#0f172a' }}>{center.name}</div>
                      <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{center.centerId}</div>
                    </td>

                    {/* Stato */}
                    <td style={tdStyle}>
                      <span style={{
                        display: 'inline-block', padding: '3px 10px', borderRadius: '99px',
                        fontSize: '0.75rem', fontWeight: '600',
                        backgroundColor: st.bg, color: st.text,
                      }}>
                        {st.label}
                      </span>
                      {center.graceDaysLeft !== null && (
                        <div style={{ fontSize: '0.7rem', color: '#ef4444', marginTop: '2px' }}>
                          {center.graceDaysLeft}gg rimasti
                        </div>
                      )}
                    </td>

                    {/* Piano */}
                    <td style={tdStyle}>
                      <span style={{ fontWeight: '600', textTransform: 'capitalize' }}>
                        {center.planId || '—'}
                      </span>
                      {center.billingInterval && (
                        <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                          {center.billingInterval === 'monthly' ? 'Mensile' : 'Annuale'}
                        </div>
                      )}
                    </td>

                    {/* SMS (mese) — conteggio semplice, live sui log */}
                    <td style={{ ...tdStyle, fontWeight: '600', color: '#8b5cf6' }}>
                      {center.smsSentThisMonth || 0}
                    </td>

                    {/* DICOM Toggle */}
                    <td style={tdStyle}>
                      <button
                        onClick={() => handleToggleDicom(center.centerId, center.dicomEnabled)}
                        disabled={actionLoading === `${center.centerId}:dicom`}
                        style={{
                          padding: '4px 10px', borderRadius: '99px', border: 'none',
                          fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer',
                          backgroundColor: center.dicomEnabled ? '#dcfce7' : '#f3f4f6',
                          color: center.dicomEnabled ? '#166534' : '#9ca3af',
                        }}
                      >
                        {actionLoading === `${center.centerId}:dicom`
                          ? '...'
                          : center.dicomEnabled ? 'ON' : 'OFF'
                        }
                      </button>
                    </td>

                    {/* Azioni */}
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {/* Impersona: scegli quale gestionale aprire */}
                        <button
                          onClick={() => handleImpersonate(center.centerId, 'clinico')}
                          style={actionBtnStyle('#6366f1')}
                          title="Apri Gestionale Clinico come questo centro"
                        >
                          👀 Clinico
                        </button>
                        <button
                          onClick={() => handleImpersonate(center.centerId, 'hr')}
                          style={actionBtnStyle('#6366f1')}
                          title="Apri Master HR come questo centro"
                        >
                          👀 HR
                        </button>

                        {/* Sospendi / Riattiva */}
                        <button
                          onClick={() => handleSuspendToggle(center.centerId, center.status)}
                          disabled={actionLoading === `${center.centerId}:suspend`}
                          style={actionBtnStyle(center.status === 'suspended' ? '#10b981' : '#ef4444')}
                          title={center.status === 'suspended' ? 'Riattiva centro' : 'Sospendi centro'}
                        >
                          {actionLoading === `${center.centerId}:suspend`
                            ? '...'
                            : center.status === 'suspended' ? '✅' : '⛔'
                          }
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── MODAL: Crea Nuovo Centro ─────────────────────────────────────── */}
      {newCenterModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          justifyContent: 'center', alignItems: 'center', zIndex: 9999,
        }}
          onClick={closeNewCenterModal}
        >
          <div
            style={{
              backgroundColor: 'white', borderRadius: '16px', padding: '28px',
              width: '100%', maxWidth: '440px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {newCenterResult ? (
              // ── Esito: mostra le credenziali da comunicare al cliente ──
              <>
                <h3 style={{ margin: '0 0 4px', fontSize: '1.1rem', color: '#166534' }}>
                  ✅ Centro creato con successo
                </h3>
                <p style={{ margin: '16px 0 6px', fontSize: '0.85rem', color: '#64748b' }}>
                  Comunica queste credenziali al titolare per il primo accesso:
                </p>
                <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '14px', fontSize: '0.9rem', lineHeight: 1.8 }}>
                  <div><b>Email:</b> {newOwnerEmail}</div>
                  <div><b>Password provvisoria:</b> 123456</div>
                  <div><b>ID Centro:</b> {newCenterResult.centerId}</div>
                </div>
                <p style={{ margin: '10px 0 0', fontSize: '0.75rem', color: '#92400e' }}>
                  ⚠️ Al primo login gli verrà chiesto di impostare una password personale.
                </p>
                <button onClick={closeNewCenterModal} style={{ ...btnStyle('#2563eb'), width: '100%', marginTop: '18px' }}>
                  Chiudi
                </button>
              </>
            ) : (
              // ── Form di creazione ──
              <>
                <h3 style={{ margin: '0 0 4px', fontSize: '1.1rem', color: '#0f172a' }}>
                  Crea Nuovo Centro
                </h3>
                <p style={{ margin: '0 0 20px', fontSize: '0.85rem', color: '#64748b' }}>
                  Il centro nasce vuoto (in trial), pronto per essere configurato dal titolare.
                </p>

                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  Nome Centro
                </label>
                <input
                  type="text"
                  value={newCenterName}
                  onChange={(e) => setNewCenterName(e.target.value)}
                  placeholder="es. Fisio Centro Roma"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '1rem', marginBottom: '14px', boxSizing: 'border-box' }}
                />

                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  Nome e Cognome Titolare
                </label>
                <input
                  type="text"
                  value={newOwnerName}
                  onChange={(e) => setNewOwnerName(e.target.value)}
                  placeholder="es. Mario Rossi"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '1rem', marginBottom: '14px', boxSizing: 'border-box' }}
                />

                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  Email Titolare
                </label>
                <input
                  type="email"
                  value={newOwnerEmail}
                  onChange={(e) => setNewOwnerEmail(e.target.value)}
                  placeholder="mario.rossi@email.it"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '1rem', marginBottom: '8px', boxSizing: 'border-box' }}
                />
                <p style={{ margin: '0 0 20px', fontSize: '0.75rem', color: '#94a3b8' }}>
                  Password provvisoria fissa: <b>123456</b> — gli verrà chiesto di cambiarla al primo accesso.
                </p>

                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={closeNewCenterModal}
                    style={{ ...btnStyle('#f3f4f6'), color: '#4b5563', flex: 1 }}
                  >
                    Annulla
                  </button>
                  <button
                    onClick={handleCreateCenter}
                    disabled={!newCenterName.trim() || !newOwnerName.trim() || !newOwnerEmail.trim() || newCenterLoading}
                    style={{ ...btnStyle('#16a34a'), flex: 1, opacity: (!newCenterName.trim() || !newOwnerName.trim() || !newOwnerEmail.trim()) ? 0.5 : 1 }}
                  >
                    {newCenterLoading ? '...' : 'Crea Centro'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      </div>
  );
}

// ── Componente Stats Card ─────────────────────────────────────────────────
function StatCard({ label, value, color }) {
  return (
    <div style={{
      backgroundColor: 'white', borderRadius: '12px', padding: '16px 18px',
      border: '1px solid #e5e7eb',
    }}>
      <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600', marginBottom: '6px' }}>
        {label}
      </div>
      <div style={{ fontSize: '1.5rem', fontWeight: '700', color }}>
        {value}
      </div>
    </div>
  );
}

// ── Stili ──────────────────────────────────────────────────────────────────
const thStyle = {
  padding: '12px 14px', textAlign: 'left', fontWeight: '600',
  color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const tdStyle = {
  padding: '12px 14px', verticalAlign: 'middle',
};

const btnStyle = (bg) => ({
  padding: '8px 16px', borderRadius: '8px', border: 'none',
  backgroundColor: bg, color: 'white', fontWeight: '600',
  fontSize: '0.85rem', cursor: 'pointer',
});

const actionBtnStyle = (borderColor) => ({
  padding: '6px 10px', borderRadius: '8px',
  border: `1px solid ${borderColor}20`, backgroundColor: `${borderColor}10`,
  cursor: 'pointer', fontSize: '0.9rem', lineHeight: 1,
});